import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Button, Divider, LinearProgress, Popover, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    AutoAwesomeRounded,
    DescriptionRounded,
    EventNoteRounded,
    GeneratingTokensRounded,
    ImageRounded,
    InsightsRounded,
} from '@mui/icons-material';
import type { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { AndieUsageDocument } from '@/graphql/generated/andie-usage.generated';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';
import { useSession } from '@/providers/session-provider';

/** Percent-used at which the balance is considered "low" (matches the backend warning threshold). */
const LOW_THRESHOLD = 80;
const HOVER_OPEN_DELAY_MS = 350;
const HOVER_CLOSE_DELAY_MS = 120;

type Usage = NonNullable<ReturnType<typeof useAndieUsage>['usage']>;

/**
 * Event a PostHog workflow listens on to alert the team when a workspace runs out of Andie credits.
 *
 * Captured here rather than in swonkie-nest on purpose. Once a workspace is blocked the composer is
 * disabled, so the backend chat gate can never be reached again for exactly the workspaces we want to
 * hear about; the usage meter is the one place that sees the wall every time.
 */
const CREDITS_LIMIT_REACHED_EVENT = 'andie.credits.limit-reached';

/**
 * Limits already reported in this page session. `useAndieUsage` is called twice (the balance chip and
 * the chat input) and refetches on every mount, so without this a single wall reports several times.
 * The workflow masks on the same three values, so this is about event volume, not correctness.
 */
const reportedLimits = new Set<string>();

/**
 * Shared accessor for the current workspace's Andie usage. Both the chip and the chat input read it;
 * Apollo dedupes the request and shares the cache entry. network-only so a changed limit / fresh usage
 * is reflected on every mount (drawer reopen), not only after a chat turn.
 */
export function useAndieUsage() {
    const { data, loading } = useQuery(AndieUsageDocument, { fetchPolicy: 'network-only' });
    const usage = data?.andieUsage;
    const { workspace } = useSession();
    const workspaceId = workspace?.publicId;

    useEffect(() => {
        if (!POSTHOG_ENABLED || !usage?.blocked || workspaceId === undefined || workspaceId === null) {
            return;
        }

        // The workflow keys its dedup on exactly these three, so a refresh stays silent while a new
        // period, or an allowance changed by an upgrade, reads as a genuinely new alert.
        // periodResetsAt is a non-null DateTime scalar and arrives as an ISO string, so it is already
        // the exact value the workflow hashes on.
        const key = `${workspaceId}:${usage.periodResetsAt}:${usage.creditLimit}`;
        if (reportedLimits.has(key)) return;
        reportedLimits.add(key);

        // The workspace group is already set in session-provider, so this is group-scoped for free.
        posthog.capture(CREDITS_LIMIT_REACHED_EVENT, {
            workspaceId,
            usedCredits: usage.usedCredits,
            creditLimit: usage.creditLimit,
            remainingCredits: usage.remainingCredits,
            percentageUsed: usage.percentageUsed,
            periodResetsAt: usage.periodResetsAt,
        });
    }, [usage, workspaceId]);

    return { usage, loading };
}

/** Status colour by consumption: andie (healthy) → warning (low) → error (depleted/blocked). */
const statusColor = (theme: Theme, percentageUsed: number, blocked: boolean): string => {
    if (blocked || percentageUsed >= 100) return theme.palette.error.main;
    if (percentageUsed >= LOW_THRESHOLD) return theme.palette.warning.main;
    return theme.palette.andie.main;
};

function ActivityRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
    return (
        <Stack direction="row" alignItems="center" gap={1.25}>
            <Box sx={{ color: 'text.disabled', display: 'inline-flex' }}>{icon}</Box>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
                {value.toLocaleString()}
            </Typography>
        </Stack>
    );
}

function UsagePopoverContent({ usage }: { usage: Usage }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const percentageUsed = Math.round(usage.percentageUsed);
    const remaining = Math.max(0, Math.round(usage.remainingCredits));
    const used = Math.round(usage.usedCredits);
    const limit = usage.creditLimit;
    const resetsAt = new Date(usage.periodResetsAt).toLocaleDateString(i18n.language, {
        day: 'numeric',
        month: 'long',
    });

    return (
        <Stack sx={{ width: 264, p: 1, gap: 1.75 }}>
            <Stack gap={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                        {t('andie.usage.credits-title', { defaultValue: 'Andie credits' })}
                    </Typography>

                    <Typography variant="caption" color="text.disabled">
                        {t('andie.usage.resets', { defaultValue: 'Resets {date}', date: resetsAt })}
                    </Typography>
                </Stack>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(100, percentageUsed)}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            backgroundColor: (theme) => statusColor(theme, percentageUsed, usage.blocked),
                        },
                    }}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="subtitle2"
                        sx={{ color: (theme) => statusColor(theme, percentageUsed, usage.blocked) }}
                    >
                        {t('andie.usage.balance', {
                            defaultValue: '{credits} left',
                            credits: remaining.toLocaleString(),
                        })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('andie.usage.used-counter', {
                            defaultValue: '{used} / {limit} used',
                            used: used.toLocaleString(),
                            limit: limit.toLocaleString(),
                        })}
                    </Typography>
                </Stack>
            </Stack>

            <Divider />

            <Stack gap={1.25}>
                <ActivityRow
                    icon={<ImageRounded sx={{ fontSize: 18 }} />}
                    label={t('andie.usage.images', { defaultValue: 'Images generated' })}
                    value={usage.imagesGenerated}
                />
                <ActivityRow
                    icon={<DescriptionRounded sx={{ fontSize: 18 }} />}
                    label={t('andie.usage.briefings', { defaultValue: 'Post drafts written' })}
                    value={usage.briefingsGenerated}
                />
                <ActivityRow
                    icon={<EventNoteRounded sx={{ fontSize: 18 }} />}
                    label={t('andie.usage.plans', { defaultValue: 'Content plans' })}
                    value={usage.contentPlansGenerated}
                />
                <ActivityRow
                    icon={<InsightsRounded sx={{ fontSize: 18 }} />}
                    label={t('andie.usage.insights', { defaultValue: 'Insights analyzed' })}
                    value={usage.insightsAnalyzed}
                />
            </Stack>

            <Divider />

            <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={<AutoAwesomeRounded sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/settings/workspace/billing#upgrade')}
                sx={{ borderRadius: 1.5, textTransform: 'none' }}
            >
                {t('andie.usage.upgrade-cta', { defaultValue: 'Get more credits' })}
            </Button>
        </Stack>
    );
}

/**
 * Andie credits balance chip shown by the chat send button: a coin + remaining credits, tinted by status
 * so you can read your balance at a glance. Hovering it (after a short delay) opens the full breakdown.
 * Renders nothing until usage loads.
 */
export default function AndieUsageMeter() {
    const { t } = useTranslation();
    const { usage } = useAndieUsage();
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const openTimer = useRef<ReturnType<typeof setTimeout>>();
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();

    const scheduleOpen = useCallback(() => {
        clearTimeout(closeTimer.current);
        openTimer.current = setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
    }, []);
    const scheduleClose = useCallback(() => {
        clearTimeout(openTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
    }, []);

    useEffect(
        () => () => {
            clearTimeout(openTimer.current);
            clearTimeout(closeTimer.current);
        },
        [],
    );

    if (!usage) return null;

    const percentageUsed = Math.round(usage.percentageUsed);
    const remaining = Math.max(0, Math.round(usage.remainingCredits));
    const label = t('andie.usage.aria', {
        defaultValue: 'Andie credits: {credits} remaining',
        credits: remaining.toLocaleString(),
    });

    return (
        <>
            <Box
                ref={anchorRef}
                role="img"
                aria-label={label}
                tabIndex={0}
                onMouseEnter={scheduleOpen}
                onMouseLeave={scheduleClose}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    pl: 0.75,
                    pr: 1,
                    py: 0.25,
                    borderRadius: 999,
                    cursor: 'default',
                    outline: 'none',
                    color: (theme) => statusColor(theme, percentageUsed, usage.blocked),
                    bgcolor: (theme) => alpha(statusColor(theme, percentageUsed, usage.blocked), 0.12),
                    transition: 'background-color 180ms ease, color 180ms ease',
                    '&:hover, &:focus-visible': {
                        bgcolor: (theme) => alpha(statusColor(theme, percentageUsed, usage.blocked), 0.2),
                    },
                }}
            >
                <GeneratingTokensRounded sx={{ fontSize: 16 }} />
                <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1, letterSpacing: 0.2 }}>
                    {percentageUsed}%
                </Typography>
            </Box>
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                disableScrollLock
                sx={{ pointerEvents: 'none' }}
                slotProps={{
                    paper: {
                        onMouseEnter: scheduleOpen,
                        onMouseLeave: scheduleClose,
                        sx: {
                            pointerEvents: 'auto',
                            mt: -1,
                            borderRadius: 2,
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            boxShadow: (theme) => theme.shadows[6],
                            overflow: 'visible',
                        },
                    },
                }}
            >
                <UsagePopoverContent usage={usage} />
            </Popover>
        </>
    );
}
